import React, { useEffect, useRef, useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import Button from "aldoo-ra/Button"
import { useCategories } from "../../hooks/useCatalogData"
import { buildCategoryPath } from "../../utils/categories"
import { useBrowsedPath, useCategoryTrail } from "../../hooks/useCategoryTrail"

// aldoo.com > Parent > Child [> item] — the way the visitor came: the path in
// the url on a category page, `path` when the page knows it (a product opened
// from a category), else the category's first parents.
// On a phone the trail rarely fits: it stays on one line and scrolls
// sideways (swipe), opened at its end — where the page is — so the tail is
// what shows first; from 600px it wraps as before.
export default function CategoryBreadcrumb({ categoryId, itemName, path: followed, className = "" }) {
  const { list } = useCategories()
  const trail = useCategoryTrail()
  const path = followed?.length ? followed : trail && trail[trail.length - 1].categoryId === categoryId ? trail : buildCategoryPath(categoryId, list)
  const ref = useRef(null)
  // which way there is more: the fades and arrows at the edges say so
  const [more, setMore] = useState({ left: false, right: false })
  const measure = () => {
    const el = ref.current
    if (!el) return
    setMore({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 })
  }
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.scrollLeft = el.scrollWidth
    measure()
    el.addEventListener("scroll", measure, { passive: true })
    window.addEventListener("resize", measure)
    return () => {
      el.removeEventListener("scroll", measure)
      window.removeEventListener("resize", measure)
    }
  }, [categoryId, itemName, path.length])
  const nudge = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.6, behavior: "smooth" })
  if (path.length === 0 && !itemName) return null

  const link = "text-[14px] text-text_secondary hover:text-primary hover:underline"
  const sep = <ChevronRightIcon className="w-3 h-3 text-primary stroke-2 mx-1 shrink-0" />

  return (
    <nav aria-label="Breadcrumb" className={twMerge("relative", className)}>
      {/* the edges: a fade and an arrow where there is more, over a margin
          the text keeps clear of, so the first and last crumb stay readable */}
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => nudge(-1)}
        className={twMerge("tab:hidden absolute left-0 top-0 bottom-0 z-10 w-10 flex items-center justify-start bg-gradient-to-r from-paper via-paper/90 to-transparent transition-opacity", more.left ? "opacity-100" : "opacity-0 pointer-events-none")}
      >
        <ChevronLeftIcon className="w-4 h-4 text-text stroke-2" />
      </button>

      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => nudge(1)}
        className={twMerge("tab:hidden absolute right-0 top-0 bottom-0 z-10 w-10 flex items-center justify-end bg-gradient-to-l from-paper via-paper/90 to-transparent transition-opacity", more.right ? "opacity-100" : "opacity-0 pointer-events-none")}
      >
        <ChevronRightIcon className="w-4 h-4 text-text stroke-2" />
      </button>

      <ol ref={ref} className="flex flex-nowrap tab:flex-wrap items-center m-0 p-0 list-none overflow-x-auto tab:overflow-visible no-scrollbar whitespace-nowrap tab:whitespace-normal">
        {/* the room the arrows take at either end, on a phone */}
        <li aria-hidden className="tab:hidden shrink-0 w-6" />
        <li className="flex items-center">
          <Button 
            variant="none" 
            href="/categories" 
            className={link}
          >
            aldoo.com
          </Button>
        </li>
        {path.map((cat, index) => {
          const isLast = index === path.length - 1
          const url = "/category/" + path.slice(0, index + 1).map((c) => c.categoryId).join("/")
          return (
            <li key={cat.categoryId} className="flex items-center">
              {sep}
              {isLast && !itemName ? (
                <span className="text-[14px] text-text font-medium">
                  {cat.name}
                </span>
              ) : (
                <Button 
                  variant="none" 
                  href={url} 
                  className={link}
                >
                  {cat.name}
                </Button>
              )}
            </li>
          )
        })}
        {itemName && (
          <li className="flex items-center">
            {sep}
            <span className="text-[14px] text-text font-medium">
              {itemName}
            </span>
          </li>
        )}
        <li aria-hidden className="tab:hidden shrink-0 w-6" />
      </ol>
    </nav>
  )
}

/**
 * A product's trail: the category path the visitor came down when the product
 * is in it (or under it), else its first category's.
 */
export function ProductBreadcrumb({ product, itemName, className }) {
  const followed = useBrowsedPath(product?.category)
  if (!product?.category?.length) return null
  return <CategoryBreadcrumb categoryId={product.category[0]} path={followed} itemName={itemName} className={className} />
}
